'use client';

import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Button } from '../components/ui/button';
import { Check, Building2, ShieldCheck, Zap } from 'lucide-react';

export default function EnterprisePage() {
    return (
        <div className="flex min-h-screen flex-col bg-black text-white selection:bg-violet-500/30">
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[50%] h-[50%] rounded-full bg-cyan-900/20 blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[50%] h-[50%] rounded-full bg-violet-900/20 blur-[150px]" />
            </div>

            <Navbar />

            <main className="flex-1 relative z-10">
                {/* Hero */}
                <section className="pt-32 pb-20 container px-4 md:px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6">
                            Talktrail Enterprise
                        </span>
                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8">
                            Where community drives{' '}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                                success
                            </span>
                        </h1>
                        <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-10">
                            Our Enterprise plan is designed for communities that require robust, tailored experiences, offering the advanced customization, control, and confidence that your organization demands.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button className="h-12 px-8 rounded-full bg-white text-black hover:bg-zinc-200 text-lg font-semibold">
                                Contact Enterprise Sales
                            </Button>
                        </div>
                    </motion.div>
                </section>

                {/* Features */}
                <section className="py-24 bg-zinc-900/30 border-y border-white/5">
                    <div className="container px-4 md:px-6">
                        <div className="grid md:grid-cols-2 gap-16 items-center">
                            <div className="space-y-8">
                                <h2 className="text-4xl font-bold">Custom Design & Development</h2>
                                <div className="space-y-6">
                                    <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                                        <h3 className="text-xl font-bold mb-2 text-violet-300">Custom Design</h3>
                                        <p className="text-zinc-400">Our designers will work with you to help you realize your vision. We can match your organization's brand guidelines and create a custom theme.</p>
                                    </div>
                                    <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                                        <h3 className="text-xl font-bold mb-2 text-cyan-300">Custom Development</h3>
                                        <p className="text-zinc-400">Our developers will create a seamless experience for your members, building custom plugins and integrations tailored to your specific needs.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-cyan-600 blur-[60px] opacity-20" />
                                <div className="relative rounded-2xl border border-white/10 bg-black/50 overflow-hidden shadow-2xl">
                                    <img src="/placeholder_dashboard.png" className="w-full h-auto opacity-100" style={{ minHeight: "300px", background: "linear-gradient(45deg, #111 0%, #222 100%)" }} alt="Dashboard Preview" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Zap className="w-16 h-16 text-white/20" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Value Props */}
                <section className="py-24 container px-4 md:px-6">
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: 'Migration Support', desc: 'Our team assists you in migrating content and member data securely.' },
                            { title: 'You Own Your Data', desc: 'Full ownership of your knowledge assets on the DKG. No lock-in.' },
                            { title: '99.9% Uptime', desc: 'Reliable service SLA suitable for mission-critical communities.' },
                            { title: 'Enterprise Security', desc: 'Built with privacy in mind. Compliant with industry standards.' },
                            { title: 'Fast Priority Support', desc: 'Get rapid responses from our global support team spanning 16 time zones.' },
                            { title: 'Safe Staging', desc: 'Test changes in safe environments before deploying to your real community.' },
                        ].map((prop, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-violet-500/30 transition-all text-center md:text-left"
                            >
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-4 mx-auto md:mx-0">
                                    <Check className="w-5 h-5 text-emerald-400" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{prop.title}</h3>
                                <p className="text-zinc-400 text-sm leading-relaxed">{prop.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* CTA */}
                <section className="py-24 container px-4 md:px-6 text-center">
                    <div className="p-12 rounded-[2rem] bg-gradient-to-br from-violet-900/20 to-cyan-900/20 border border-white/10">
                        <h2 className="text-3xl font-bold mb-6">Ready to take the next step?</h2>
                        <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">Plans start at <span className="text-white font-bold">$5,000</span>. Get expert answers on features, pricing, data migration and more.</p>
                        <Button className="h-12 px-8 rounded-full bg-white text-black hover:bg-zinc-200 font-bold">
                            Contact Enterprise Sales
                        </Button>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
